import dayjs from "../../shared utils/dayjsConfig.js";

// Advisories shown in the board's persistent warnings panel when a resident is
// placed into / removed from a room mid-stay. Valid but unusual actions, so we
// never block them — we surface what the single-night board can't show.
// Messages are returned WITHOUT the resident's name; the panel prefixes it.
//
// When the resident carries `bedroomAllocations` (their rooms across the whole
// stay, all campuses — see docs/backend-ticket-bedroom-resident-allocations.md)
// the warnings are PRECISE: we know exactly which nights are covered and by
// which room. Without that data we fall back to stay-date-only logic, and
// deliberately stay quiet on placement (rather than guess and cry wolf about
// nights that are actually already allocated).

function fmt(date) {
  return dayjs(date).format("ddd D MMM");
}

function datesInRange(from, to) {
  const start = dayjs(from);
  const end = dayjs(to);
  if (!start.isValid() || !end.isValid() || start.isAfter(end, "day")) return [];

  const out = [];
  for (let cur = start; !cur.isAfter(end, "day"); cur = cur.add(1, "day")) {
    out.push(cur.format("YYYY-MM-DD"));
  }
  return out;
}

// A stay's `stay_end` is the DEPARTURE date (the morning they leave), not a
// night in the room — the same half-open convention the booking model uses
// (`arrival <= booking.start < departure`), and why the residents feed stops
// returning someone on their departure date. So the nights that actually need
// a bed run stay_start .. stay_end - 1; asking for a room on the departure
// night flags gaps nobody can fill (the person isn't on that night's board).
function lastNightOf(stayEnd) {
  if (!stayEnd) return null;
  const end = dayjs(stayEnd);
  return end.isValid() ? end.subtract(1, "day").format("YYYY-MM-DD") : null;
}

function stayNights(stayStart, stayEnd) {
  const lastNight = lastNightOf(stayEnd);
  return lastNight ? datesInRange(stayStart, lastNight) : [];
}

function rangeLabel(dates) {
  if (!dates.length) return "";
  if (dates.length === 1) return fmt(dates[0]);
  return `${fmt(dates[0])} to ${fmt(dates[dates.length - 1])}`;
}

// Collapse a list of YYYY-MM-DD strings into readable contiguous ranges,
// e.g. ["…05","…06","…07","…15"] -> "Sun 5 Jul to Tue 7 Jul, Wed 15 Jul".
function summariseDateRanges(dates) {
  if (!dates.length) return "";
  const sorted = [...dates].sort();
  const ranges = [];
  let runStart = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i < sorted.length; i += 1) {
    if (dayjs(sorted[i]).diff(dayjs(prev), "day") === 1) {
      prev = sorted[i];
    } else {
      ranges.push([runStart, prev]);
      runStart = sorted[i];
      prev = sorted[i];
    }
  }
  ranges.push([runStart, prev]);

  return ranges
    .map(([a, b]) => (a === b ? fmt(a) : `${fmt(a)} to ${fmt(b)}`))
    .join(", ");
}

// Map of YYYY-MM-DD -> { bedroomId, bedroomName } across the stay, or null when
// no allocation data is available (the signal to fall back). `from`/`to` are the
// stay's start and DEPARTURE dates; coverage is clipped to the nights in
// between (see lastNightOf) — allocations often run one night past departure
// because that's how the board saves them, and those nights aren't ours.
function coverageByDate(allocations, from, to, houseById) {
  if (!Array.isArray(allocations)) return null;

  const map = new Map();
  allocations.forEach((a) => {
    if (!a) return;
    const house = houseById ? houseById.get(String(a.bedroomId)) : undefined;
    datesInRange(a.startDate, a.endDate || a.startDate).forEach((d) => {
      map.set(d, { bedroomId: a.bedroomId, bedroomName: a.bedroomName, house });
    });
  });
  // Restrict to the stay window so out-of-stay rows don't leak in.
  if (from && to) {
    const window = new Set(stayNights(from, to));
    for (const key of [...map.keys()]) {
      if (!window.has(key)) map.delete(key);
    }
  }
  return map;
}

// "McNabb east 3" when the house is known, otherwise just "east 3".
function roomDisplay(name, house) {
  const label = name || "a room";
  return house ? `${house} ${label}` : label;
}

function roomLabel(room) {
  if (!room) return "a room";
  return roomDisplay(
    room.bedroomName || room.roomName || room.groupName,
    room.house
  );
}

// Is `targetRoom` free for the resident's WHOLE stay? Aggregates every OTHER
// resident's saved bedroomAllocations into a per-night occupancy of the room and
// checks capacity + gender + type across stay_start..stay_end. Drives the
// "give them this room for the whole stay" offer on a mid-stay placement.
// Reflects SAVED state (unsaved board edits aside), which is what "is this room
// really free?" should mean. Returns { available, blockedSummary }.
export function buildRoomStayAvailability({ resident, targetRoom, allResidents }) {
  if (!resident || !targetRoom) return { available: false, blockedSummary: null };

  const nights = stayNights(resident.stay_start, resident.stay_end);
  if (!nights.length) return { available: true, blockedSummary: null };

  const capacity = Number(targetRoom.capacity) || 0;
  const roomId = String(targetRoom.id);
  const selfId = String(resident.id);

  // date -> [{ gender, type }] of the other people in the room that night
  const occByNight = new Map();
  (allResidents || []).forEach((r) => {
    if (!r || String(r.id) === selfId) return;
    (r.bedroomAllocations || []).forEach((a) => {
      if (!a || String(a.bedroomId) !== roomId) return;
      datesInRange(a.startDate, a.endDate || a.startDate).forEach((d) => {
        if (!occByNight.has(d)) occByNight.set(d, []);
        occByNight.get(d).push({ gender: r.gender, type: r.type });
      });
    });
  });

  const blocked = nights.filter((d) => {
    const occ = occByNight.get(d) || [];
    if (capacity > 0 && occ.length >= capacity) return true;
    if (resident.gender && occ.some((o) => o.gender && o.gender !== resident.gender)) return true;
    if (resident.type && occ.some((o) => o.type && o.type !== resident.type)) return true;
    return false;
  });

  return {
    available: blocked.length === 0,
    blockedSummary: blocked.length ? summariseDateRanges(blocked) : null,
  };
}

// Given the backend's first-over-capacity night and the start the placement
// will actually be saved from (viewed date, or stay_start when the operator
// chose whole-stay), work out the clamp window offered on an over-capacity
// rejection: the room is free from `intendedStart` through the day BEFORE
// `firstUnavailableDate`. `canClamp` is false when there are no free leading
// nights (the room is already full on the first night of the span) — then only
// "revert" makes sense.
export function computeClampWindow(firstUnavailableDate, intendedStart) {
  if (!firstUnavailableDate || !intendedStart) {
    return { freeThrough: null, canClamp: false };
  }
  const freeThroughDay = dayjs(firstUnavailableDate).subtract(1, "day");
  const start = dayjs(intendedStart);
  if (!freeThroughDay.isValid() || !start.isValid()) {
    return { freeThrough: null, canClamp: false };
  }
  return {
    freeThrough: freeThroughDay.format("YYYY-MM-DD"),
    canClamp: !freeThroughDay.isBefore(start, "day"),
  };
}

export function buildPlacementWarnings({
  resident,
  selectedDate,
  targetRoom,
  fromRoom,
  houseById,
}) {
  const warnings = [];
  if (!resident || !targetRoom) return warnings;

  const date = dayjs(selectedDate);
  const start = resident.stay_start ? dayjs(resident.stay_start) : null;
  const lastNight = lastNightOf(resident.stay_end);
  const end = lastNight ? dayjs(lastNight) : null;

  const isMove = fromRoom && String(fromRoom.id) !== String(targetRoom.id);
  const midStay = start && start.isValid() && date.isAfter(start, "day");

  // 1) Changing an already-roomed student's room partway through their stay.
  //    The headline "are you sure" case — they'll be split across two rooms.
  //    (No need for coverage data; this is about the action itself.)
  if (isMove && midStay) {
    warnings.push(
      `changing room mid-stay — ${roomLabel(fromRoom)} → ${roomLabel(targetRoom)} ` +
        `from ${fmt(date)}. Earlier nights stay in ${roomLabel(fromRoom)}.`
    );
  }

  const coverage = coverageByDate(
    resident.bedroomAllocations,
    resident.stay_start,
    resident.stay_end,
    houseById
  );

  // The remaining checks need cross-night data; without it we don't guess.
  if (coverage === null) return warnings;

  // 2) Fresh placement (from Unassigned) that leaves earlier nights of the
  //    stay without a room. Not relevant to a move — the room they're leaving
  //    already covers those nights.
  if (!isMove && start && start.isValid()) {
    const uncovered = datesInRange(start, date.subtract(1, "day")).filter(
      (d) => !coverage.has(d)
    );
    if (uncovered.length) {
      warnings.push(
        `arrives ${fmt(start)} — ${rangeLabel(uncovered)} still ` +
          `${uncovered.length > 1 ? "need" : "needs"} a room.`
      );
    }
  }

  // 3) Genuine clash: already has a room on nights in the new span in some
  //    OTHER room — excluding the target and (on a move) the room being
  //    vacated. Group the clashing nights by room and summarise into ranges so
  //    it reads "7 Jul to 22 Jul (east 5)" rather than a list of every night.
  if (end && end.isValid()) {
    const fromId = isMove ? String(fromRoom.id) : null;
    const byRoom = new Map();
    datesInRange(date, end).forEach((d) => {
      const c = coverage.get(d);
      if (!c) return;
      const id = String(c.bedroomId);
      if (id === String(targetRoom.id) || id === fromId) return;
      if (!byRoom.has(id)) {
        byRoom.set(id, {
          name: c.bedroomName || "another room",
          house: c.house,
          dates: [],
        });
      }
      byRoom.get(id).dates.push(d);
    });
    if (byRoom.size) {
      const parts = [...byRoom.values()].map(
        (r) => `${summariseDateRanges(r.dates)} (${roomDisplay(r.name, r.house)})`
      );
      warnings.push(`already has a room ${parts.join(", ")}.`);
    }
  }

  return warnings;
}

export function buildRemovalWarnings({ resident, selectedDate, fromRoom }) {
  const warnings = [];
  if (!resident) return warnings;

  const date = dayjs(selectedDate);
  // Their last night, not the departure date — removing the room on the day
  // they leave costs them nothing.
  const lastNight = lastNightOf(resident.stay_end);
  const end = lastNight ? dayjs(lastNight) : null;
  if (!end || !end.isValid() || date.isAfter(end, "day")) return warnings;

  const coverage = coverageByDate(
    resident.bedroomAllocations,
    resident.stay_start,
    resident.stay_end
  );

  if (coverage === null) {
    // Stay-only fallback: removing on D releases the room through departure.
    const span = end.isSame(date, "day") ? fmt(date) : `${fmt(date)} to ${fmt(end)}`;
    warnings.push(
      `resident until ${fmt(end)} — removing this room from ${fmt(date)} ` +
        `leaves ${span} with no room.`
    );
    return warnings;
  }

  // Precise: which nights from D onward have no room once THIS one is released?
  // (Coverage reflects saved state, so the room being removed still appears —
  // exclude it; a night kept by some other room is not a gap.)
  const fromId = fromRoom ? String(fromRoom.id) : null;
  const gap = datesInRange(date, end).filter((d) => {
    const covered = coverage.get(d);
    return !covered || String(covered.bedroomId) === fromId;
  });

  if (gap.length) {
    warnings.push(
      `resident until ${fmt(end)} — ${rangeLabel(gap)} ` +
        `${gap.length > 1 ? "have" : "has"} no room after this.`
    );
  }

  return warnings;
}

// Standing audit (not action-triggered): a resident who is PART-allocated —
// has at least one room but a gap somewhere in their stay. Returns a summary
// of the uncovered nights, or null when fully covered or not applicable.
// Residents with NO allocations are intentionally excluded — they already sit
// in the Unassigned pool; this is for the easy-to-miss incomplete ones.
export function buildCoverageGap(resident, houseById) {
  if (!resident) return null;

  const allocations = resident.bedroomAllocations;
  if (!Array.isArray(allocations) || allocations.length === 0) return null;

  const coverage = coverageByDate(
    allocations,
    resident.stay_start,
    resident.stay_end,
    houseById
  );
  if (!coverage) return null;

  const nights = stayNights(resident.stay_start, resident.stay_end);
  if (!nights.length) return null;

  const gaps = nights.filter((d) => !coverage.has(d));
  if (!gaps.length) return null;

  // Surface where they ARE roomed: group covered nights by room (in stay
  // order) and summarise each into ranges, e.g. "east 3 (28 Jun to 4 Jul),
  // east 5 (5 Jul, 7 Jul to 22 Jul)".
  const byRoom = new Map();
  nights.forEach((d) => {
    const c = coverage.get(d);
    if (!c) return;
    const id = String(c.bedroomId);
    if (!byRoom.has(id)) {
      byRoom.set(id, { name: c.bedroomName || "a room", house: c.house, dates: [] });
    }
    byRoom.get(id).dates.push(d);
  });
  const allocated = [...byRoom.values()]
    .map((r) => `${roomDisplay(r.name, r.house)} (${summariseDateRanges(r.dates)})`)
    .join(", ");

  return `no room ${summariseDateRanges(gaps)} — allocated ${allocated}.`;
}
